import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DefaultStyles from '../../../constants/DefaultFormStyles';
import FieldModes from '../../../constants/FieldModes';
import FieldTypes from '../../../constants/FieldTypes';
import { isEmptyValue } from '../../../lib/validation';

const StyledWrapper = styled.div`
  height: ${props => props.measure ? 'auto' : '100%'};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledBlock = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.backgroundColor || DefaultStyles.backgroundColor};
  font-size:${(props) => (props.fontSize !== undefined ? `${props.fontSize}px` : DefaultStyles.fontSize)} !important;
  color: ${(props) => props.fontColor || DefaultStyles.fontColor};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  overflow: ${(props) => (props.textOverflow ? 'hidden;' : 'visible;')}
  border-radius: ${(props) => (props.borderRadius !== undefined ? `${props.borderRadius}px` : DefaultStyles.borderRadius)};
  border: ${(props) => props.border};
  padding: ${(props) => (props.padding !== undefined ? `${props.padding}px` : DefaultStyles.padding)};

  /*
   * Tight box (see render): one line of text does not fit inside the vertical padding, so the
   * vertical padding goes and the line is centred by its line-height, the way TextField centres
   * a plain wrapping field in a short box since NO-2373. Horizontal padding stays so the text
   * does not shift sideways. The rule sits on this node rather than on the text so the edit
   * textarea, which inherits font and line-height, draws its caret line on the same pixels.
   * No overflow or clip change: a 14px glyph run on a 16px line stays inside the box, and a
   * block that already overhangs its box overhangs exactly as it does today. Keyed on $tight
   * alone; $tightLineHeight is the box height and always positive when $tight is true.
   */
  ${(props) => (props.$tight
    ? `padding-top: 0; padding-bottom: 0; line-height: ${props.$tightLineHeight}px;`
    : '')}
  resize: none;
`;

const StyledTextarea = styled.textarea`
  border: none;
  width: 100%;
  height: 100%;
  resize: none;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  text-decoration: inherit;
  background-color: transparent;

  /*
   * Tight box editor (see StyledBlock and render). The idle line is the block's content box,
   * centred by the inherited line-height. The browser gives a textarea 2px of padding and lays
   * an inline-block out on the baseline, which in a roomy block happens to put its first line on
   * the idle line (measured: 6..18 both ways) but in a tight block put it 2px low. With the
   * vertical padding gone and block layout, the first line starts at the content top like the
   * idle line, so nothing moves on click. Horizontal padding stays, as on the block itself.
   */
  ${(props) => (props.$tight ? 'display: block; padding-top: 0; padding-bottom: 0;' : '')}
  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`;

const StyledDisplayText = styled.div`
  white-space: pre-wrap; /* Preserve line breaks */
  width: 100%;
  height: 100%;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  text-decoration: inherit;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class BlockField extends React.PureComponent {

  static propTypes = {

    fieldPositionId: PropTypes.string,

    value: PropTypes.string,
    disabled: PropTypes.bool,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Page blocks only (Field.js): allow the tight-box centring rule, see render
    tightBoxEnabled: PropTypes.bool,

  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
      boxClientHeight: null, //the block node's clientHeight: inside the border, padding included
      textLineCount: null, //how many lines the display text draws
      lineHeightPixels: null, //one natural line box for this font, read off the wrapper
    };
    this.blockNode = null; //StyledBlock DOM node
    this.displayTextNode = null; //StyledDisplayText DOM node, null while the editor is open
    this.boxResizeObserver = null;
    this.observedNode = null;
  }

  /**
   * The three numbers the tight-box rule needs (see render), read off the drawn nodes.
   *
   * lineHeightPixels is one line box for this font. It is NOT read from the block node or the
   * text, because once the rule is on those draw with line-height equal to the box, and a block
   * that then grew a little would read the tight value back and never turn roomy again. The
   * wrapper above StyledBlock is never restyled by the rule and inherits the form's line-height
   * (1 today, GridLayout's StyledIMPORTANT, and the PDF renderer sets the same), so its
   * line-height divided by its font size is the ratio the text is drawn with. Times the
   * block's own font size gives the natural line box. If the ratio is unreadable
   * (line-height: normal), 1.2 is the browser's usual value.
   *
   * textLineCount divides the text's scroll height by the line-height it is drawn with. That
   * number is the same whether the box is tight or not, because both heights scale together,
   * provided the box is at least one natural line tall (render guards that). Only meaningful
   * for a populated value: StyledDisplayText is height: 100%, so an empty one reports its
   * forced box, not zero, and would round to one line. A trailing newline in the value
   * ("Inspector:\n") does not make a second line: Chrome draws no line box after a final
   * preserved newline (measured, 12px for both "Inspector:" and "Inspector:\n").
   *
   * While the editor is open the display text is unmounted, so the last count is kept and
   * the box does not change under the caret. The blur that closes the editor re-reads it.
   *
   * boxClientHeight is the padding box of a pinned node and is unchanged by the rule removing
   * the vertical padding, except for a slot shorter than twice the padding, where border-box
   * sizing floors the node at its padding; render keeps the rule off there (roomHeight > 0).
   *
   * setState only when a number changed, so this settles after one extra render.
   *
   * @private
   */
  readBoxGeometry = () => {

    const node = this.blockNode;
    const { fontSize, value, tightBoxEnabled } = this.props;

    /**
     * Nothing to read outside the rule's scope: table cells (no tightBoxEnabled) and empty
     * spacer blocks, which documents carry by the dozen. A block that just lost its text keeps
     * stale numbers here; render ignores them through hasPopulatedValue.
     */
    if (!node || !tightBoxEnabled || isEmptyValue(value, FieldTypes.block)) return;

    const { wrapperRef, boxClientHeight, textLineCount, lineHeightPixels } = this.state;

    const size = typeof fontSize === 'number' ? fontSize : parseInt(DefaultStyles.fontSize, 10);

    let nextLineHeight = lineHeightPixels;
    if (wrapperRef) {
      const wrapperStyle = getComputedStyle(wrapperRef);
      const ratio = parseFloat(wrapperStyle.lineHeight) / parseFloat(wrapperStyle.fontSize);
      nextLineHeight = Math.round(size * (Number.isFinite(ratio) && ratio > 0 ? ratio : 1.2));
    }

    //null (not yet drawn) while the editor is open on first mount; the previous count otherwise
    let nextLineCount = textLineCount;
    const text = this.displayTextNode;
    if (text) {
      const drawnLineHeight = parseFloat(getComputedStyle(text).lineHeight);
      nextLineCount = drawnLineHeight > 0 ? Math.round(text.scrollHeight / drawnLineHeight) : null;
    }

    const nextClientHeight = node.clientHeight;

    if (nextClientHeight !== boxClientHeight || nextLineCount !== textLineCount || nextLineHeight !== lineHeightPixels) {
      this.setState({ boxClientHeight: nextClientHeight, textLineCount: nextLineCount, lineHeightPixels: nextLineHeight });
    }

  };

  /**
   * Watch the block node so a grid resize re-runs the rule once react-grid-layout's 200ms
   * height animation has finished; a read in the ref callback right after the drag sees the
   * old box. Same shape as TextField.syncDisplayObserver: attached and detached from the
   * lifecycle only, never from a ref callback, and only for a populated page block on screen.
   * pdf mode never resizes, so the ref read is enough there.
   *
   * @private
   */
  syncBoxObserver = () => {

    const { fieldMode, tightBoxEnabled, value } = this.props;

    const eligible = !!tightBoxEnabled
      && fieldMode !== FieldModes.pdf
      && !isEmptyValue(value, FieldTypes.block)
      && !!this.blockNode
      && typeof ResizeObserver !== 'undefined';

    if (!eligible) {
      this.disconnectBoxObserver();
      return;
    }

    if (this.observedNode === this.blockNode) return;

    this.disconnectBoxObserver();
    this.observedNode = this.blockNode;
    this.boxResizeObserver = new ResizeObserver(() => this.readBoxGeometry());
    this.boxResizeObserver.observe(this.blockNode);

  };

  /** @private */
  disconnectBoxObserver = () => {
    if (this.boxResizeObserver) this.boxResizeObserver.disconnect();
    this.boxResizeObserver = null;
    this.observedNode = null;
  };

  componentDidMount() {
    this.syncBoxObserver();
  }

  componentWillUnmount() {
    this.disconnectBoxObserver();
  }

  componentDidUpdate(prevProps) {

    const { wrapperRef } = this.state;
    const { value, measure, onMeasureCompleted, measuredHeight, measuredOn, updatedOn, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (prevProps.value !== value) {
      requestAnimationFrame(() => {
        this.setState({ isEditingHeight: wrapperRef.getBoundingClientRect().height });
      });
    }

    // Block fields should only be measured if they have a populated value
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.block);
    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && measure && onMeasureCompleted && hasPopulatedValue && refHeightAvailable && (!measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn))) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
        this.setState({ isEditingHeight: refHeightAvailable });
      });
    }

    this.syncBoxObserver();

  }

  render() {

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      disabled,
      fieldMode,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      measure,
      onMeasureCompleted,
      value,
      tightBoxEnabled,
    } = this.props;

    const { isEditing, isEditingHeight, wrapperRef } = this.state;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const bWidth = borderWidth !== undefined ? `${borderWidth}px` : DefaultStyles.borderWidth;
    const bColor = borderColor || 'transparent';
    const border = `${bWidth} solid ${bColor}`;
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.block);

    const { boxClientHeight, textLineCount, lineHeightPixels } = this.state;

    /**
     * Pinned: the block fills its grid slot. It is unpinned only in the exporter's measuring
     * pass (measure WITH a handler), where the wrapper is height: auto and clientHeight is the
     * text's own height. The rule stays off there so the height the exporter stores is the one
     * it stores today. Same expression as the wrapper's measure prop below.
     */
    const isPinned = !(measure && hasPopulatedValue && !!onMeasureCompleted);

    /**
     * Tight box, page blocks only (tightBoxEnabled). One line of text does not fit inside the
     * vertical padding, so the vertical padding goes and the line is centred by its line-height
     * (StyledBlock), the way TextField centres a plain wrapping field in a short box since
     * NO-2373. The room is computed from the padding PROP, not the drawn padding, so the rule
     * removing that padding cannot change its own answer (the same guard TextField documents).
     * Multi-line blocks are excluded because they overflow visibly today, and squashing every
     * line to the room's line-height would overlap them; a text field never has this case
     * because it clips to one line.
     */
    const normalizedPadding = typeof padding === 'number' ? padding : parseInt(DefaultStyles.padding, 10);
    const roomHeight = Number.isFinite(boxClientHeight) ? boxClientHeight - (2 * normalizedPadding) : null;

    /**
     * Two guards keep the rule to boxes that can hold one line at all, and they only work as
     * a pair (measured in the browser while this was planned):
     *
     * boxClientHeight >= lineHeightPixels: the box must be at least one natural line tall.
     * Below that the tight line-height would be smaller than the font, the centred glyphs hang
     * past the line box, scrollHeight counts them, and the line count reads 1 under natural
     * styling but 2 under tight styling (16px font in an 8px box: 16 -> 12 scrollHeight, 1 -> 2
     * lines). The rule would toggle every render, and because readBoxGeometry sets state from
     * a ref callback during commit, React throws "Maximum update depth exceeded": a crash, not
     * a flicker. With the guard the glyphs never overhang the line box by more than a pixel and
     * the count is 1 under both stylings.
     *
     * roomHeight > 0: a slot shorter than its own padding. There border-box sizing floors the
     * padded node at twice the padding, so clientHeight DOES change when the padding goes, and
     * the first guard alone would create a new loop. The room in that case is exactly 0, so
     * this guard keeps the rule off before the first one is ever consulted.
     */
    const isTightBox = !!tightBoxEnabled
      && hasPopulatedValue
      && isPinned
      && textLineCount === 1
      && Number.isFinite(lineHeightPixels)
      && Number.isFinite(roomHeight)
      && roomHeight > 0
      && boxClientHeight >= lineHeightPixels
      && lineHeightPixels > roomHeight;

    //Guarded above: when tight, the box is at least one line tall, so this is always a positive number
    const tightLineHeight = isTightBox ? boxClientHeight : null;

    return (
      <StyledWrapper 
        /**
         * Unpinned (height: auto) only while a measurement is being collected, which is the pass
         * that supplies onMeasureCompleted. The exporter's final pass passes measure without a
         * handler; there the block fills its grid slot as it does in the builder, instead of
         * drawing a box the size of its text inside an empty slot.
         */
        {...{ margin, measure: measure && hasPopulatedValue && !!onMeasureCompleted }}
        ref={(e) => {
          if (e && !wrapperRef && !isEditingHeight) this.setState({ wrapperRef: e, isEditingHeight: e.getBoundingClientRect().height });
        }}
      >
        <StyledBlock
          data-testid={`${fieldPositionId}-block-field`}
          border={border}
          $tight={isTightBox}
          $tightLineHeight={tightLineHeight}
          ref={(e) => {
            this.blockNode = e;
            //Every render, like TextField's display ref: cheap, and settles after one extra render
            if (e) this.readBoxGeometry();
          }}
          {...{
            disabled,

            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
          }}
          onClick={this.handleEdit}
        >
          {!isEditing || fieldMode === FieldModes.readonly ? (
            <StyledDisplayText ref={(e) => { this.displayTextNode = e; }}>{value}</StyledDisplayText>
          ) : (
            <StyledTextarea
              $tight={isTightBox}
              defaultValue={value}
              rows={2}
              style={{ height: isEditingHeight ? `${isEditingHeight}px` : 'auto' }}
              onBlur={this.handleBlur}
              readOnly={fieldMode !== FieldModes.edit}
              autoFocus
            />
          )}
        </StyledBlock>
      </StyledWrapper>
    );
  }

  handleEdit = () => {
    this.setState({ isEditing: true });
  }

  handleBlur = (e) => {

    const { onChange, fieldId, fieldIdentifier, fieldPositionId, disabled } = this.props;
    
    if (onChange && !disabled) {
      onChange(fieldId, fieldIdentifier, fieldPositionId, e.target.value);
      this.setState({ isEditing: false });
    }
  }

}

export default BlockField;
